import './Badge.css'

/**
 * Status badges and type tags (status colours option C, 2026-11-02).
 *
 * Every badge carries an icon AND a word — never colour alone. Colours come
 * only from the role variables in styles/status-colors.css, via the
 * .esl-status--<role> classes in Badge.css; nothing here names a value.
 *
 *   role      used for
 *   info      Open
 *   success   Allocated
 *   warning   Date pending · data > 4 h
 *   urgent    (reserved)
 *   danger    Past ESL promise · data > 24 h
 *   neutral   Complete, and any status string not listed here
 *   outline   type tags: Cancelled, Return, Shared (not a status)
 */
export type StatusRole = 'info' | 'success' | 'warning' | 'urgent' | 'danger' | 'neutral'

export type IconName =
  | 'open'
  | 'allocated'
  | 'complete'
  | 'cancelled'
  | 'return'
  | 'shared'
  | 'pending'
  | 'alert'
  | 'status'

/** 16px stroke icons, currentColor, decorative (the word carries meaning). */
export function Icon({ name }: { name: IconName }) {
  const common = {
    width: 14,
    height: 14,
    viewBox: '0 0 16 16',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
    className: 'esl-icon',
  }
  switch (name) {
    case 'open':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="5.5" fill="currentColor" />
        </svg>
      )
    case 'allocated':
      return (
        <svg {...common}>
          <path d="M2.5 5 8 2.5 13.5 5v6L8 13.5 2.5 11z" />
          <path d="M2.5 5 8 7.5 13.5 5M8 7.5v6" />
        </svg>
      )
    case 'complete':
      return (
        <svg {...common}>
          <path d="m3 8.5 3.25 3.25L13 5" />
        </svg>
      )
    case 'cancelled':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="5.5" />
          <path d="m4.2 11.8 7.6-7.6" />
        </svg>
      )
    case 'return':
      return (
        <svg {...common}>
          <path d="M6 3.5 2.5 7 6 10.5" />
          <path d="M2.5 7h7a3 3 0 0 1 0 6H8" />
        </svg>
      )
    case 'shared':
      return (
        <svg {...common}>
          <circle cx="5.5" cy="5.5" r="2.25" />
          <circle cx="11" cy="6.5" r="1.75" />
          <path d="M1.75 13.5a3.75 3.75 0 0 1 7.5 0M9.5 13.5a3 3 0 0 1 5 -2.2" />
        </svg>
      )
    case 'pending':
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="5.5" />
          <path d="M8 5v3.25l2 1.25" />
        </svg>
      )
    case 'alert':
      return (
        <svg {...common}>
          <path d="M8 2.25 14.25 13.5H1.75z" />
          <path d="M8 6.5v3M8 11.6v.01" />
        </svg>
      )
    case 'status':
    default:
      return (
        <svg {...common}>
          <circle cx="8" cy="8" r="2.5" fill="currentColor" stroke="none" />
        </svg>
      )
  }
}

export function StatusBadge({ role, icon, label }: { role: StatusRole; icon: IconName; label: string }) {
  return (
    <span className={`esl-status esl-status--${role}`}>
      <Icon name={icon} />
      {label}
    </span>
  )
}

export function TypeTag({ icon, label }: { icon: IconName; label: string }) {
  return (
    <span className="esl-tag">
      <Icon name={icon} />
      {label}
    </span>
  )
}

/**
 * A P21 order or line status as a badge. Known values get their role;
 * anything else renders as-is, neutral, so a new status string is shown
 * rather than hidden or guessed at. Cancelled is a type tag, not a colour.
 */
export function RawStatusBadge({ status }: { status: string | null }) {
  if (!status) return null
  switch (status) {
    case 'Open':
      return <StatusBadge role="info" icon="open" label="Open" />
    case 'Allocated':
      return <StatusBadge role="success" icon="allocated" label="Allocated" />
    case 'Complete':
      return <StatusBadge role="neutral" icon="complete" label="Complete" />
    case 'Cancelled':
      return <TypeTag icon="cancelled" label="Cancelled" />
    default:
      return <StatusBadge role="neutral" icon="status" label={status} />
  }
}
